import { FC, memo, useContext } from 'react';
import { Polyline } from 'react-leaflet';
import LicenseContext from 'src/contexts/LicenseContext';
import { EditPlaceEntity } from 'src/entities/EditPlace.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { PlanningsOperationDelivery } from 'src/models/PlanningsOperationGroup.model';
import { datetimeUtil } from 'src/utils/datetime.util';

type Props = {
  editPlaces: EditPlaceEntity[];
  delivery: PlanningsDeliveryEntity;
  selectedCycleIndexes: SelectedCycle;
  planningsOperationDelivery: PlanningsOperationDelivery;
  deliveryEntities: PlanningsDeliveryEntity[];
}

const PlanningMapDeliveryPolylinesPresenter: FC<Props> = memo((
  {
    editPlaces,
    delivery,
    selectedCycleIndexes,
    planningsOperationDelivery,
    deliveryEntities,
  }
) => {
  const licenseContext = useContext(LicenseContext);
  if (!delivery || !planningsOperationDelivery) return null;

  const garagePosition: PositionEntity = [parseFloat(delivery.garageLatitude), parseFloat(delivery.garageLongitude)];

  const plotPositions: PositionEntity[] = [];
  const filledCycleIndexes = planningsOperationDelivery.cycles.filter((it) => !it.empty).map((it) => it.cycleIndex);

  planningsOperationDelivery.cycles.forEach((cycle) => {
    let operations = cycle.allOperations();

    if (editPlaces) {
      const editPlace = editPlaces.find((it) => it.deliveryId === delivery.id && it.cycleIndex === cycle.cycleIndex);
      if (editPlace) {
        operations = editPlace.places.flatMap((it) => it.allOperations());
      }
    }
    if (operations.length === 0) { return; }

    if (selectedCycleIndexes.cycleIndexes.includes(cycle.cycleIndex)) {
      if (delivery.departureOperation && cycle.cycleIndex === Math.min(...filledCycleIndexes)) {
        plotPositions.push(garagePosition);
      }
      operations.forEach((ops) => {
        plotPositions.push([parseFloat(ops.latitude), parseFloat(ops.longitude)]);
      });
      if (delivery.arrivalOperation) {
        if (licenseContext.config?.use_trunk_transportation) {
          const sorted = operations.sort((a, b) => new Date(b.departureAt).getTime() - new Date(a.departureAt).getTime());
          const isLastDay = datetimeUtil.isSameDay(new Date(delivery.endAt), new Date(sorted[0].departureAt));
          if (isLastDay) {
            plotPositions.push(garagePosition);
          }
        } else if (cycle.cycleIndex === Math.max(...filledCycleIndexes)) {
          plotPositions.push(garagePosition);
        }
      } else {
        // 車庫に帰らない場合、次の勤務を探してそこまでの線を引く。
        const nextDelivery = deliveryEntities.filter((it) => it.driverId === delivery.driverId && it.truckId === delivery.truckId && it.id !== delivery.id && new Date(it.startAt).getTime() > new Date(delivery.startAt).getTime()).sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())[0];
        if (nextDelivery && nextDelivery.operations.length > 0) {
          const firstOperation = nextDelivery.operations.sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())[0];
          plotPositions.push([parseFloat(firstOperation.latitude), parseFloat(firstOperation.longitude)]);
        }
      }
    }
  });

  return (
    <Polyline
      positions={plotPositions}
      pathOptions={{ color: delivery.driverColor }}
    />
  );
});

export default PlanningMapDeliveryPolylinesPresenter;
